'use client';

import {
  type CSSProperties,
  createContext,
  forwardRef,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
  useContext,
} from 'react';
import { cva, cx, type VariantProps } from '../../utils/cva';

const tableStyles = cva({
  base: 'w-full border-separate border-spacing-0 text-sm',
});

const tableHeaderStyles = cva({
  base: 'bg-default text-default font-semibold text-sm',
});

const tableRowStyles = cva({
  base: 'transition-colors [tbody_&:hover]:bg-default-hover',
  variants: {
    interactive: {
      true: 'cursor-pointer',
    },
  },
});

const baseCellStyles = 'text-sm text-default px-3 py-2';

const tableCellStyles = cva({
  base: baseCellStyles,
  variants: {
    align: {
      start: 'text-left',
      end: 'text-right',
    },
    variant: {
      default: 'border-b border-subtle [tbody_tr:last-child_&]:border-b-0',
      floating: '',
    },
    size: {
      single: 'h-14',
      multi: 'h-[72px]',
    },
  },
  defaultVariants: {
    align: 'start',
    variant: 'default',
    size: 'single',
  },
});

const tableHeaderCellStyles = cva({
  base: cx(baseCellStyles, 'h-12 font-semibold border-b border-subtle'),
  variants: {
    align: {
      start: 'text-left',
      end: 'text-right',
    },
  },
  defaultVariants: {
    align: 'start',
  },
});

const TableVariantContext = createContext<'default' | 'floating'>('default');
const TableTestIdContext = createContext<string | null>(null);

const useTableTestId = (
  explicitTestId: string | null | undefined,
  suffix: string,
): string | undefined => {
  const baseTestId = useContext(TableTestIdContext);

  // If explicitly provided, use it (converting null to undefined)
  if (explicitTestId !== undefined) {
    return explicitTestId || undefined;
  }

  // Otherwise, append suffix to base test ID (which defaults to 'table' if not provided)
  return baseTestId ? `${baseTestId}-${suffix}` : undefined;
};

export type TableProps = HTMLAttributes<HTMLTableElement> & {
  bordered?: boolean;
  maxHeight?: CSSProperties['maxHeight'];
  variant?: 'default' | 'floating';
  'data-testid': string | null;
  'aria-label'?: string;
  'aria-labelledby'?: string;
};

export const Table = forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      bordered = false,
      maxHeight,
      variant = 'default',
      children,
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...props
    },
    ref,
  ) => {
    const finalTestId = !dataTestId ? 'table' : dataTestId;

    return (
      <TableVariantContext.Provider value={variant}>
        <TableTestIdContext.Provider value={finalTestId}>
          <div
            className={cx(
              'relative w-full',
              bordered ? 'rounded-lg border border-subtle overflow-hidden' : '',
            )}
          >
            <div
              className="relative w-full overflow-auto"
              style={maxHeight ? { maxHeight } : undefined}
            >
              <table
                ref={ref}
                className={cx(tableStyles(), className)}
                data-testid={dataTestId || undefined}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                {...props}
              >
                {children}
              </table>
            </div>
          </div>
        </TableTestIdContext.Provider>
      </TableVariantContext.Provider>
    );
  },
);

export type TableHeaderProps = HTMLAttributes<HTMLTableSectionElement> & {
  'data-testid'?: string | null;
};

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  TableHeaderProps
>(({ className, 'data-testid': dataTestId, ...props }, ref) => {
  const testId = useTableTestId(dataTestId, 'header');

  return (
    <thead
      ref={ref}
      className={cx(tableHeaderStyles(), className)}
      data-testid={testId}
      {...props}
    />
  );
});

export type TableBodyProps = HTMLAttributes<HTMLTableSectionElement> & {
  'data-testid'?: string | null;
};

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, 'data-testid': dataTestId, ...props }, ref) => {
    const testId = useTableTestId(dataTestId, 'body');

    return (
      <tbody ref={ref} className={className} data-testid={testId} {...props} />
    );
  },
);

export type TableRowProps = HTMLAttributes<HTMLTableRowElement> & {
  interactive?: boolean;
  'data-testid'?: string | null;
};

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  (
    { className, interactive, 'data-testid': dataTestId, onClick, ...props },
    ref,
  ) => {
    const testId = useTableTestId(dataTestId, 'row');

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (
        interactive &&
        onClick &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        onClick(event as unknown as React.MouseEvent<HTMLTableRowElement>);
      }
    };

    return (
      <tr
        ref={ref}
        className={cx(tableRowStyles({ interactive }), className)}
        data-testid={testId}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        {...props}
      />
    );
  },
);

const CellContent = ({
  align,
  children,
}: {
  align?: 'start' | 'end';
  children: React.ReactNode;
}) => (
  <div className={cx('flex items-center', align === 'end' && 'justify-end')}>
    {children}
  </div>
);

export type TableHeadProps = Omit<
  ThHTMLAttributes<HTMLTableCellElement>,
  'align'
> &
  VariantProps<typeof tableHeaderCellStyles> & {
    'data-testid'?: string | null;
  };

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  (
    { className, align, children, 'data-testid': dataTestId, ...props },
    ref,
  ) => {
    const testId = useTableTestId(dataTestId, 'head');

    return (
      <th
        ref={ref}
        className={cx(tableHeaderCellStyles({ align }), className)}
        data-testid={testId}
        {...props}
      >
        <CellContent align={align}>{children}</CellContent>
      </th>
    );
  },
);

export type TableCellProps = Omit<
  TdHTMLAttributes<HTMLTableCellElement>,
  'align'
> &
  VariantProps<typeof tableCellStyles> & {
    'data-testid'?: string | null;
  };

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  (
    { className, align, size, children, 'data-testid': dataTestId, ...props },
    ref,
  ) => {
    const variant = useContext(TableVariantContext);
    const testId = useTableTestId(dataTestId, 'cell');

    return (
      <td
        ref={ref}
        className={cx(tableCellStyles({ align, variant, size }), className)}
        data-testid={testId}
        {...props}
      >
        <CellContent align={align}>{children}</CellContent>
      </td>
    );
  },
);
